import { useEffect, useMemo, useState } from "react";
import {
  Bell,
  Building2,
  Crown,
  DollarSign,
  Save,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import "../styles/pages/configuracoes.css";

function dataFirebase(valor) {
  return valor?.toDate?.()?.toLocaleDateString("pt-BR") || "-";
}

const moedas = [
  { value: "BRL", label: "Real brasileiro", symbol: "R$" },
  { value: "USD", label: "Dólar americano", symbol: "$" },
  { value: "GBP", label: "Libra esterlina", symbol: "£" },
];

const formatosData = [
  { value: "pt-BR", label: "Brasil — dd/mm/aaaa" },
  { value: "en-US", label: "Estados Unidos — mm/dd/yyyy" },
  { value: "en-GB", label: "Reino Unido — dd/mm/yyyy" },
];

const mesesInicio = [
  { value: "01", label: "Janeiro" },
  { value: "02", label: "Fevereiro" },
  { value: "03", label: "Março" },
  { value: "04", label: "Abril" },
  { value: "05", label: "Maio" },
  { value: "06", label: "Junho" },
  { value: "07", label: "Julho" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Setembro" },
  { value: "10", label: "Outubro" },
  { value: "11", label: "Novembro" },
  { value: "12", label: "Dezembro" },
];

export default function Configuracoes({ uid, config, billing }) {
  const [nomeNegocio, setNomeNegocio] = useState(config?.nomeNegocio || "Meu negócio");
  const [moeda, setMoeda] = useState(config?.moeda || "BRL");
  const [alertaGastos, setAlertaGastos] = useState(config?.alertaGastos || "80");
  const [metaEconomia, setMetaEconomia] = useState(config?.metaEconomia || "20");
  const [formatoData, setFormatoData] = useState(config?.formatoData || "pt-BR");
  const [mesInicio, setMesInicio] = useState(config?.mesInicio || "01");
  const [notificacoes, setNotificacoes] = useState(config?.notificacoes ?? true);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");

  useEffect(() => {
    setNomeNegocio(config?.nomeNegocio || "Meu negócio");
    setMoeda(config?.moeda || "BRL");
    setAlertaGastos(String(config?.alertaGastos || "80"));
    setMetaEconomia(String(config?.metaEconomia || "20"));
    setFormatoData(config?.formatoData || "pt-BR");
    setMesInicio(config?.mesInicio || "01");
    setNotificacoes(config?.notificacoes ?? true);
  }, [
    config?.nomeNegocio,
    config?.moeda,
    config?.alertaGastos,
    config?.metaEconomia,
    config?.formatoData,
    config?.mesInicio,
    config?.notificacoes,
  ]);

  const moedaAtual = useMemo(
    () => moedas.find((item) => item.value === moeda) || moedas[0],
    [moeda]
  );

  async function salvarConfiguracoes(e) {
    e.preventDefault();

    if (!uid) {
      setErro("Usuário não encontrado. Faça login novamente.");
      return;
    }

    const alertaNumero = Number(alertaGastos);
    const metaNumero = Number(metaEconomia);

    if (Number.isNaN(alertaNumero) || alertaNumero < 1 || alertaNumero > 100) {
      setErro("O alerta de gastos precisa ficar entre 1% e 100%.");
      return;
    }

    if (Number.isNaN(metaNumero) || metaNumero < 0 || metaNumero > 100) {
      setErro("A meta de economia precisa ficar entre 0% e 100%.");
      return;
    }

    setSalvando(true);
    setMensagem("");
    setErro("");

    try {
      await setDoc(
        doc(db, "usuarios", uid, "config", "app"),
        {
          nomeNegocio: nomeNegocio.trim() || "Meu negócio",
          moeda,
          alertaGastos: alertaNumero,
          metaEconomia: metaNumero,
          formatoData,
          mesInicio,
          notificacoes,
          tema: "dark",
          atualizadoEm: serverTimestamp(),
        },
        { merge: true }
      );

      document.documentElement.setAttribute("data-theme", "dark");
      localStorage.setItem("invexa-theme", "dark");

      setMensagem("Configurações salvas com sucesso.");
    } catch (error) {
      console.error(error);
      setErro("Não foi possível salvar as configurações agora.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="config-page">
      <div className="config-header">
        <div>
          <span className="config-eyebrow">AJUSTES</span>
          <h1>Configurações</h1>
          <p>Gerencie nome, moeda, alertas e preferências do Invexa.</p>
        </div>

        <div className="config-header-icon">
          <Settings size={25} />
        </div>
      </div>

      <div className="config-grid">
        <div className="config-card">
          <span>Nome do negócio</span>
          <strong>{config?.nomeNegocio || "Meu negócio"}</strong>
        </div>

        <div className="config-card">
          <span>Moeda atual</span>
          <strong>{moedaAtual.symbol} {moedaAtual.value}</strong>
        </div>

        <div className="config-card">
          <span>Status</span>
          <strong>{billing?.status || "-"}</strong>
        </div>

        <div className="config-card">
          <span>Teste até</span>
          <strong>{dataFirebase(billing?.trialEndsAt)}</strong>
        </div>
      </div>

      <form className="config-form-card" onSubmit={salvarConfiguracoes}>
        <div className="config-form-heading">
          <div>
            <span className="config-eyebrow">PERSONALIZAÇÃO</span>
            <h2>Preferências principais</h2>
            <p>Essas informações ficam salvas no seu usuário.</p>
          </div>

          <div className="config-form-icon">
            <SlidersHorizontal size={22} />
          </div>
        </div>

        <div className="config-form-grid">
          <label className="config-field">
            <span>Nome do negócio</span>
            <input
              type="text"
              value={nomeNegocio}
              onChange={(e) => setNomeNegocio(e.target.value)}
              placeholder="Ex: Gustavo"
            />
          </label>

          <label className="config-field">
            <span>Moeda padrão</span>
            <select value={moeda} onChange={(e) => setMoeda(e.target.value)}>
              {moedas.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.symbol} — {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className="config-field">
            <span>Alerta de gastos (%)</span>
            <input
              type="number"
              min="1"
              max="100"
              value={alertaGastos}
              onChange={(e) => setAlertaGastos(e.target.value)}
              placeholder="80"
            />
          </label>

          <label className="config-field">
            <span>Meta de economia (%)</span>
            <input
              type="number"
              min="0"
              max="100"
              value={metaEconomia}
              onChange={(e) => setMetaEconomia(e.target.value)}
              placeholder="20"
            />
          </label>

          <label className="config-field">
            <span>Formato de data</span>
            <select value={formatoData} onChange={(e) => setFormatoData(e.target.value)}>
              {formatosData.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className="config-field">
            <span>Mês inicial do planejamento</span>
            <select value={mesInicio} onChange={(e) => setMesInicio(e.target.value)}>
              {mesesInicio.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="config-switch-card">
          <div className="config-switch-left">
            <div className="config-switch-icon">
              <Bell size={20} />
            </div>

            <div>
              <strong>Notificações inteligentes</strong>
              <p>Use para futuros avisos de gastos, metas e vencimentos.</p>
            </div>
          </div>

          <button
            className={notificacoes ? "config-switch active" : "config-switch"}
            type="button"
            onClick={() => setNotificacoes((atual) => !atual)}
            aria-label="Ativar ou desativar notificações"
          >
            <i />
          </button>
        </div>

        <div className="config-preview-grid">
          <div className="config-preview">
            <div className="config-preview-icon green">
              <Building2 size={20} />
            </div>

            <div>
              <span>Negócio</span>
              <strong>{nomeNegocio || "Meu negócio"}</strong>
              <p>Nome usado no topo e nos relatórios.</p>
            </div>
          </div>

          <div className="config-preview">
            <div className="config-preview-icon blue">
              <DollarSign size={20} />
            </div>

            <div>
              <span>Moeda</span>
              <strong>{moedaAtual.symbol} {moedaAtual.value}</strong>
              <p>{moedaAtual.label}</p>
            </div>
          </div>

          <div className="config-preview">
            <div className="config-preview-icon purple">
              <ShieldCheck size={20} />
            </div>

            <div>
              <span>Segurança</span>
              <strong>Modo premium escuro</strong>
              <p>Visual oficial mantido para máxima consistência.</p>
            </div>
          </div>
        </div>

        {mensagem && <div className="config-success">{mensagem}</div>}
        {erro && <div className="config-error">{erro}</div>}

        <button className="config-save-btn" type="submit" disabled={salvando}>
          <Save size={18} />
          {salvando ? "Salvando..." : "Salvar configurações"}
        </button>
      </form>

      <div className="config-premium">
        <div className="config-premium-icon">
          <Crown size={26} />
        </div>

        <div>
          <strong>Invexa Finance Premium</strong>
          <p>Sem propagandas, experiência premium e recursos avançados.</p>
        </div>
      </div>
    </section>
  );
}
